"use client";

import Image from "next/image";
import { useId, useState } from "react";

type QA = {
  q: string;
  a: string;
  name?: string;        // "Charles"
  city?: string;        // "New York"
  avatarSrc?: string;   // "/images/people/charles.jpg" (optional)
};

const FAQS: QA[] = [
  {

    q: "What does FeelGood actually do?",
    a: "FeelGood gives you a clear picture of how you’re feeling right now—your energy, mood, sleep, stress, digestion, and overall balance—and turns that into simple, personalized steps you can apply immediately.",
    // avatarSrc: "/images/people/charles.jpg",
  },
  {

    q: "Do I need any labs or medical tests to use FeelGood?",
    a: "No. You can use the platform fully without any testing. Our goal is to help you feel better based on how you’re doing today, using actionable guidance anyone can apply.",
  },
  {

    q: "When would deeper testing be suggested?",
    a: "If your inputs show certain patterns—low energy, chronic stress load, stubborn weight issues, poor recovery, or signs of hormonal imbalance—FeelGood may recommend looking deeper.",
  },
  {

    q: "What type of deeper insights are available?",
    a: "If you choose to explore further, the platform can highlight things like: Possible nutrient deficiencies, Metabolic slow-down patterns, Stress system overload, Hormone irregularities, Inflammation signals, Sleep/recovery imbalances. These are insight suggestions, not diagnoses. The system helps you understand what might be affecting how you feel.",
  },
  {

    q: "Is this a medical service?",
    a: "No. FeelGood is a wellness and optimization platform. It focuses on lifestyle, habits, nutrition, stress reduction, and self-understanding—not medical treatment.",
  },
  {

    q: "How accurate are the insights?",
    a: "Your insights come from pattern recognition across thousands of data points and real user trends. They’re designed to be clear, helpful signals—not medical judgment.",
  },
  {

    q: "How quickly do people usually feel better?",
    a: "Most users notice improvements within 1–3 weeks when following their personalized plan consistently. Some feel changes even sooner.",
  },
  {

    q: "Do my answers stay private?",
    a: "Yes. Your information stays encrypted and never sold. You control everything inside your dashboard.",
  },
  {
 
    q: "How often should I check in?",
    a: "Most people recalibrate their plan every 2–4 weeks or anytime their lifestyle changes. You can update your inputs as often as you want—the platform adjusts instantly.",
  },
  {

    q: "What makes FeelGood different?",
    a: "We don’t start with lab orders or medical jargon. We start with you—your energy, your stress, your mood, your digestion, your habits—and build a simple, personalized plan that helps you feel better quickly.",
  },
];

export default function FAQ() {
  return (
    <section aria-labelledby="faq-heading" id="faqs" className="w-full">
      <div className="mx-auto max-w-4xl px-6 py-16 md:py-24">
        <header className="mb-10 text-center">
          <h2 id="faq-heading" className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Frequently Asked Questions
          </h2>
          <p className="mt-3 text-slate-600">
            Get answers to common questions about our care and treatments.
          </p>
        </header>

        <ul className="space-y-4">
          {FAQS.map((item, i) => (
            <li key={i}>
              <FAQItem {...item} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FAQItem({ q, a, name, city, avatarSrc }: QA) {
  const [open, setOpen] = useState(false);
  const contentId = useId();

  return (
    <div
      className={[
        "rounded-xl",
        
        "transition-colors",
      ].join(" ")}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between gap-4 px-6 py-8 text-left focus:outline-none"
      >
        <div className="flex items-center gap-4 flex-1">
          {/* Optional avatar bubble */}
          {avatarSrc && (
            <span className="relative block h-10 w-10 shrink-0 overflow-hidden rounded-full ring-2 ring-white">
              <Image
                src={avatarSrc}
                alt={`${name ?? "Person"} avatar`}
                fill
                sizes="40px"
                className="object-cover"
              />
            </span>
          )}

          <div className="flex-1">
            {name && city && (
              <p className="text-sm text-[#52363a]">
                <span className="font-medium">{name}</span> from {city} asks,
              </p>
            )}
            <h3 className="mt-0.5 text-slate-900 font-semibold text-lg">{q}</h3>
          </div>
        </div>

        {/* Plus / minus */}
        <span
          className="grid h-8 w-8 place-items-center rounded-full bg-white/70 text-slate-700"
          aria-hidden="true"
        >
          <PlusIcon className={`h-4 w-4 transition-transform ${open ? "rotate-45" : ""}`} />
        </span>
      </button>

      {/* Content with height animation */}
      <div
        id={contentId}
        role="region"
        className="px-5 overflow-hidden"
        style={{ maxHeight: open ? 500 : 0, transition: "max-height 300ms ease" }}
      >
        <div className="pb-4 text-slate-700">{a}</div>
      </div>
    </div>
  );
}

/* icon */
function PlusIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}
